import { useQuery } from '@tanstack/react-query';
import instance from '@src/api/axiosInstance';
import { isSharedView, getSessionId } from '../NexaStore';

interface ReportVersion {
  version: number;
  created_at: string;
}

interface ListVersionsResponse {
  versions?: ReportVersion[];
  reports?: ReportVersion[];
  generation_in_progress?: boolean;
  display_name?: string | null;
}

// Hook to fetch list of report versions (works for both regular and shared views)
export const useListReportVersions = () => {
  const sessionId = getSessionId();
  const isShared = isSharedView();
  const endpoint = isShared ? '/nexa/report/shared/versions' : '/nexa/report/versions';
  const params = isShared ? { share_id: sessionId } : { session_id: sessionId };

  const { data, isFetching, isError, refetch } = useQuery(
    ['nexa-report-versions', sessionId],
    async () => {
      const response = await instance.get<ListVersionsResponse>(endpoint, { params });
      const payload = response.data || {};
      const versions = payload.versions || payload.reports || [];
      return {
        versions,
        generationInProgress: payload.generation_in_progress === true,
        displayName: payload.display_name || null,
      };
    },
    {
      enabled: !!sessionId,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 60 * 1000, // 1m
      notifyOnChangeProps: ['isFetching', 'isError', 'data'],
    },
  );

  return {
    versions: data?.versions || [],
    generationInProgress: data?.generationInProgress === true,
    displayName: data?.displayName || null,
    isFetching,
    isError,
    refetch,
  };
};

// Hook to fetch a specific report version (works for both regular and shared views)
export const useGetReportVersion = (versionId: number | null) => {
  const sessionId = getSessionId();
  const isShared = isSharedView();
  const endpoint = isShared ? `/nexa/report/shared/${versionId}` : `/nexa/report/${versionId}`;
  const params = isShared ? { share_id: sessionId } : { session_id: sessionId };

  const { data, isFetching, isError } = useQuery(
    ['nexa-report-version', sessionId, versionId],
    async () => {
      const response = await instance.get(endpoint, { params });
      return response.data; // HTML content as string
    },
    {
      enabled: !!sessionId && versionId !== null,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      notifyOnChangeProps: ['isFetching', 'isError', 'data'],
    },
  );

  return { htmlContent: data as string | undefined, isFetching, isError };
};
